import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/react'
import { API_BASE_URL } from './client.js'
import { createRealtimeManager } from './realtime.js'
import { RealtimeContext } from './contexts.js'
import { useApiClient } from './useApiClient.js'

// One SSE connection per tab (lazy — opened by the first useSubscription; see
// realtime.js for the full lifecycle contract). Mount inside ApiProvider.
//
// `manager` prop is a test-only override.
export function RealtimeProvider({ children, manager: managerOverride }) {
  const { isSignedIn, getToken } = useAuth()
  const client = useApiClient()
  const getTokenRef = useRef(getToken)

  useEffect(() => {
    getTokenRef.current = getToken
  }, [getToken])

  const [manager] = useState(
    () =>
      managerOverride ||
      createRealtimeManager({
        baseUrl: API_BASE_URL,
        client,
        getToken: (opts) => getTokenRef.current?.(opts),
      }),
  )

  const [status, setStatus] = useState(manager.getStatus())
  useEffect(() => manager.onStatusChange(setStatus), [manager])

  // The SSE connection is bound to the identity that opened it — drop and
  // reopen with a fresh token whenever the signed-in state flips.
  const firstAuthRef = useRef(true)
  useEffect(() => {
    if (firstAuthRef.current) {
      firstAuthRef.current = false
      return
    }
    manager.reset()
  }, [isSignedIn, manager])

  useEffect(() => {
    manager.open() // no-op on first mount; revives after StrictMode's simulated unmount
    return () => manager.close()
  }, [manager])

  const value = useMemo(
    () => ({ manager, status, isRealtimeConnected: status === 'open' }),
    [manager, status],
  )

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
}
